import { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { StatusBadge, PassTypeBadge, formatDate } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { getPrincipalPending, principalAction } from "../lib/api";
import { Clock, CheckCircle2, Eye, Loader2 } from "lucide-react";
import TodayPastPasses from "../components/TodayPastPasses";

const PrincipalDashboard = () => {
  return (
    <DashboardLayout title="Principal Portal">
      <Routes>
        <Route index element={<PrincipalHome />} />
      </Routes>
    </DashboardLayout>
  );
};

const PrincipalHome = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data } = await getPrincipalPending();
      setPending(data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (actionType === "reject" && !rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    setSubmitting(true);
    try {
      await principalAction(selectedPass.id, {
        action: actionType,
        remarks: remarks.trim() || null,
        rejection_reason: actionType === "reject" ? rejectionReason : null
      });
      toast.success(actionType === "approve" ? "Request approved!" : "Request rejected");
      setSelectedPass(null);
      setActionType(null);
      setRemarks("");
      setRejectionReason("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="principal-dashboard">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Approval</p>
                <p className="text-2xl font-semibold text-amber-600">{pending.length}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Approved by Coordinator</p>
                <p className="text-2xl font-semibold text-emerald-600">{pending.length}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Requests Awaiting Your Approval</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : pending.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-300" />
              <p>No pending requests!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request No.</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Course/Year</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Exit Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map((req) => (
                  <TableRow key={req.id} data-testid={`pending-row-${req.id}`}>
                    <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.student_name}</p>
                        <p className="text-xs text-slate-500">{req.student_enrollment}</p>
                      </div>
                    </TableCell>
                    <TableCell>{req.course_name} - Y{req.year}</TableCell>
                    <TableCell><PassTypeBadge type={req.pass_type} /></TableCell>
                    <TableCell>{formatDate(req.exit_date)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setSelectedPass(req); setActionType(null); }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => { setSelectedPass(req); setActionType("approve"); }}
                          data-testid={`approve-btn-${req.id}`}
                        >
                          Approve
                        </Button>
                        <Button 
                          size="sm"
                          variant="destructive"
                          onClick={() => { setSelectedPass(req); setActionType("reject"); }}
                          data-testid={`reject-btn-${req.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={!!selectedPass && !!actionType} onOpenChange={() => { setSelectedPass(null); setActionType(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "Approve Request" : "Reject Request"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPass && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <p><span className="text-slate-500">Request:</span> {selectedPass.request_number}</p>
                <p><span className="text-slate-500">Student:</span> {selectedPass.student_name} ({selectedPass.course_name} - Y{selectedPass.year})</p>
                <p><span className="text-slate-500">Reason:</span> {selectedPass.reason}</p>
                <p><span className="text-slate-500">Exit:</span> {formatDate(selectedPass.exit_date)} at {selectedPass.exit_time}</p>
                <p><span className="text-slate-500">Destination:</span> {selectedPass.destination}</p>
                
                {/* Show coordinator approval */}
                {selectedPass.approval_history?.find(a => a.stage === "coordinator") && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <p className="text-emerald-600 font-medium">
                      Approved by: {selectedPass.approval_history.find(a => a.stage === "coordinator")?.approver_name}
                    </p>
                  </div>
                )}
              </div>

              {actionType === "reject" && (
                <div className="space-y-2">
                  <Label htmlFor="rejection_reason" className="text-red-600">
                    Rejection Reason *
                  </Label>
                  <Textarea
                    id="rejection_reason"
                    placeholder="Enter the reason for rejection (required)"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="border-red-200"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="remarks">Remarks (Optional)</Label>
                <Textarea
                  id="remarks"
                  placeholder="Add any remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedPass(null); setActionType(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={submitting}
              className={actionType === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              variant={actionType === "reject" ? "destructive" : "default"}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : actionType === "approve" ? (
                "Confirm Approval"
              ) : (
                "Confirm Rejection"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!selectedPass && !actionType} onOpenChange={() => setSelectedPass(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gate Pass Details</DialogTitle>
          </DialogHeader>
          {selectedPass && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Request Number</p>
                  <p className="font-mono">{selectedPass.request_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <StatusBadge status={selectedPass.status} />
                </div>
                <div>
                  <p className="text-slate-500">Student Name</p>
                  <p className="font-medium">{selectedPass.student_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Course / Year</p>
                  <p>{selectedPass.course_name} - Year {selectedPass.year}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Reason</p>
                  <p>{selectedPass.reason}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Destination</p>
                  <p>{selectedPass.destination}</p>
                </div>
              </div>
              
              {/* Approval History */}
              {selectedPass.approval_history?.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="font-medium mb-2">Approval History</p>
                  {selectedPass.approval_history.map((h, i) => (
                    <div key={i} className="text-sm p-2 bg-emerald-50 rounded mb-2">
                      <p className="font-medium">{h.approver_name}</p>
                      <p className="text-xs text-slate-500">{h.stage} - {h.action}</p>
                      {h.remarks && <p className="text-xs mt-1">Remarks: {h.remarks}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gate Pass Activity - at bottom */}
      <TodayPastPasses showPrint={true} />
    </div>
  );
};

export default PrincipalDashboard;
